import { Button, InputField, Spacer } from '../../../components/atoms';
import { createRef, useMemo, useRef, useState } from 'react';
import { APIUpload, Modal, TextEditor } from '../../../components/molecules';
import { UploadIcon, UploadIcon2 } from '../../../assets/Icons';
import {
  AddProductCON,
  FormCON,
  InputCon,
  extraStylesInput,
  AltInput,
  ImageUploadCON,
  extraStylesButton,
  TextEditorCON,
  ActionsCon,
  ManualBtnExtraStyles,
  CSVBtnExtraStyles,
  CustomSelect
} from './style';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { COMMONTYPES } from '../../../TYPES/event.types';
import { useQuery } from 'react-query';
import { useQueryClient } from 'react-query';
import { getAllcategories, getSubcategories } from '../../../network/services/goods';
import { useMutationHook } from '../../../network/mutations/useMutationHook';
import { CsvProductUpload } from './csv';
import { adminManualProductUpload } from '../../../network/services/admin/goods';
import { useNavigate } from 'react-router-dom';
import { ProductUpload } from '../../../TYPES/api.body';
import ImageDropzone from '../../../components/molecules/ImageDropzone';

const INITIAL_STATE: ProductUpload = {
  productDetails: {
    categoryId: 1,
    subCategoryId: 1,
    productName: '',
    price: '',
    quantityAvailable: '',
    minimumOrderQuantity: '',
    bulkOrderPrice: '',
    barCode: '',
    variation: '',
    size: '',
    description: ''
  },
  productImages: []
};

export interface ProductUploadResponse {
  status: string;
  description: string;
}

interface ImageState {
  image: File | string | null;
}

const INITIAL_IMAGE_STATES: ImageState[] = [
  { image: null },
  { image: null },
  { image: null },
  { image: null },
  { image: null }
];

export const AddProduct = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_STATE);

  const { data: categoriesData } = useQuery('categories', getAllcategories);
  const categories = categoriesData || [];
  const { data: subCategoriesData } = useQuery(
    ['sub_categories', { categoryId: formData.productDetails.categoryId }],
    getSubcategories,
    {
      enabled: !!formData.productDetails.categoryId
    }
  );
  const subCategories = subCategoriesData || [];
  const [imageStates, setImageStates] = useState(INITIAL_IMAGE_STATES);
  const inputRefs = useMemo(
    () =>
      Array(INITIAL_IMAGE_STATES.length)
        .fill(0)
        .map(i => createRef<HTMLInputElement>()),
    []
  );

  const [apiIsOpen, setApiIsOpen] = useState<boolean>(false);
  const [csvIsOpen, setCsvIsOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const onSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target;
    setFormData(formData => ({ ...formData, productDetails: { ...formData.productDetails, [name]: value } }));
  };

  const onInputChange = (e: COMMONTYPES['inputChange']) => {
    e.stopPropagation();
    const { name, value } = e.target;
    setFormData(formData => ({ ...formData, productDetails: { ...formData.productDetails, [name]: value } }));
  };

  const onTextEditorChange = (value: string) => {
    setFormData(formData => ({ ...formData, productDetails: { ...formData.productDetails, description: value } }));
  };

  const selectClick = (e: COMMONTYPES['divClick']) => e.stopPropagation();

  const handleFileRead = (e: COMMONTYPES['inputChange'], index: number) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageStates(prevStates => {
        const updatedStates = [...prevStates];
        updatedStates[index] = { image: file };
        return updatedStates;
      });

      setFormData(formData => ({
        ...formData,
        productImages: [...formData.productImages, file] as File[]
      }));
    }
  };

  const handleFileDelete = (index: number) => {
    setImageStates(prevStates => {
      const updatedStates = [...prevStates];
      updatedStates[index] = { image: null };
      return updatedStates;
    });

    // Remove the deleted image from the formData.productImages array
    setFormData(formData => ({
      ...formData,
      // @ts-ignore
      productImages: formData.productImages.filter((_: File | string, i:number) => i !== index) as File[]
    }));
  };

  const { mutateAsync, isLoading } = useMutationHook(adminManualProductUpload, 'PRODUCT_UPLOAD');

  const _manualProductUpload = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const payload = new FormData();

    // Append productDetails as a JSON string
    payload.append('productDetails', new Blob([JSON.stringify(formData.productDetails)], { type: 'application/json' }));

    // Append each image file to the payload
    formData.productImages.forEach(imageFile => {
      payload.append('productImages', imageFile);
    });

    try {
      const data = (await mutateAsync(payload)) as unknown as ProductUploadResponse;
      if (data.status === 'success') {
        queryClient.invalidateQueries('admin_products');
        navigate('/admin/products');
      }
    } catch (error: any) {}
  };

  const disabled =
    !formData.productImages.length ||
    !formData.productDetails.productName ||
    !formData.productDetails.price ||
    !formData.productDetails.description;

  return (
    <AddProductCON>
      {/* <CustomBreadcrumb firstLabel="Products" secondLabel="Add new products" /> */}
      <ActionsCon>
        <Button extraStyles={ManualBtnExtraStyles}>
          Manual Upload <UploadIcon />
        </Button>

        <Button onClick={() => setCsvIsOpen(true)} extraStyles={CSVBtnExtraStyles}>
          Upload CSV <UploadIcon />
        </Button>
        <Button onClick={() => setApiIsOpen(true)} extraStyles={CSVBtnExtraStyles}>
          Via API
        </Button>
      </ActionsCon>
      <FormCON>
        <AltInput>
          <InputCon>
            <span>Product Name </span>
            <InputField
              type="text"
              name="productName"
              placeholder="Name of Product"
              onChange={onInputChange}
              extraStyles={extraStylesInput}
              required={true}
              autoComplete="new-passowrd"
            />
          </InputCon>

          <InputCon>
            <span>Price</span>
            <InputField
              type="text"
              name="price"
              onChange={onInputChange}
              placeholder="Price of Product"
              extraStyles={extraStylesInput}
              required={true}
              // error={formError.password}

              autoComplete="new-passowrd"
            />
          </InputCon>
        </AltInput>

        <AltInput>
          <InputCon>
            <span>Category </span>
            <CustomSelect
              name="categoryId"
              value={formData.productDetails.categoryId}
              onChange={onSelectChange}
              onClick={selectClick}>
              {categories.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </InputCon>

          <InputCon>
            <span>Sub category</span>
            <CustomSelect
              name="subCategoryId"
              value={formData.productDetails.subCategoryId}
              onChange={onSelectChange}
              onClick={selectClick}>
              {subCategories.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </InputCon>
        </AltInput>

        <AltInput>
          <InputCon>
            <span>Quantity Available</span>
            <InputField
              type="text"
              name="quantityAvailable"
              onChange={onInputChange}
              placeholder="Quantity Available"
              extraStyles={extraStylesInput}
              required={true}
              autoComplete="new-passowrd"
            />
          </InputCon>

          <InputCon>
            <span>Minimum Order Quantity (MOQ)</span>
            <InputField
              type="text"
              name="minimumOrderQuantity"
              onChange={onInputChange}
              placeholder="Least order number"
              extraStyles={extraStylesInput}
              required={true}
              autoComplete="new-passowrd"
            />
          </InputCon>
        </AltInput>

        <AltInput>
          <InputCon>
            <span>Bulk Order Price at MOQ</span>
            <InputField
              type="text"
              name="bulkOrderPrice"
              placeholder="Bulk order price when item hits MOQ"
              extraStyles={extraStylesInput}
              onChange={onInputChange}
              required={true}
              autoComplete="new-passowrd"
            />
          </InputCon>

          <InputCon>
            <span>Bar Code</span>
            <InputField
              type="text"
              name="barCode"
              placeholder="Bulk order price when item hits MOQ"
              extraStyles={extraStylesInput}
              required={true}
              onChange={onInputChange}
              autoComplete="new-passowrd"
            />
          </InputCon>
        </AltInput>
        <AltInput>
          <InputCon>
            <span>Sub-level categories</span>
            <CustomSelect
              name="subLevelCategoryId"
              // value={formData.productDetails.subCategoryId}
              onChange={onSelectChange}
              onClick={selectClick}>
              <MenuItem value="sub-level-1">Sub-level 1</MenuItem>
              <MenuItem value="sub-level-2">Sub-level 2</MenuItem>
            </CustomSelect>
          </InputCon>
        </AltInput>
        <AltInput>
          <InputCon>
            <span>Variations</span>
            <InputField
              type="text"
              name="variation"
              placeholder="Product variations"
              extraStyles={extraStylesInput}
              onChange={onInputChange}
              required={true}
              autoComplete="new-passowrd"
            />
          </InputCon>

          <InputCon>
            <span>Size</span>
            <InputField
              type="text"
              name="size"
              placeholder="Enter size"
              extraStyles={extraStylesInput}
              required={true}
              onChange={onInputChange}
              autoComplete="new-passowrd"
            />
          </InputCon>
        </AltInput>
        <Spacer height="1rem" />
        <TextEditorCON>
          <span>Description</span>
          <TextEditor onChange={onTextEditorChange} />
        </TextEditorCON>

        <Spacer height="2rem" />

        <ImageUploadCON>
          <span>Product Images</span>
          <div className="images-con">
            <div className="primary-image-con">
              <ImageDropzone
                handleFileRead={e => handleFileRead(e, 0)}
                handleFileDelete={() => handleFileDelete(0)}
                inputRef={inputRefs[0]}
                image={imageStates[0].image}
              />
            </div>
            <div className="secondary-images-con">
              {imageStates.slice(1).map((imageState, index) => (
                <ImageDropzone
                  key={index}
                  handleFileRead={e => handleFileRead(e, index + 1)}
                  handleFileDelete={() => handleFileDelete(index + 1)}
                  inputRef={inputRefs[index + 1]}
                  image={imageState.image}
                  small
                />
              ))}
            </div>
          </div>
        </ImageUploadCON>
        <Spacer height="4rem" />
        <InputCon>
          <Button
            onClick={_manualProductUpload}
            isLoading={isLoading}
            disabled={disabled}
            extraStyles={extraStylesButton}>
            Add Product
          </Button>
        </InputCon>
      </FormCON>

      {apiIsOpen && (
        <Modal maxWidth="500px" title="Via API - Odoo.v16" onModalClose={() => setApiIsOpen(false)}>
          <APIUpload />
        </Modal>
      )}

      {csvIsOpen && <CsvProductUpload onModalClose={() => setCsvIsOpen(false)} />}
    </AddProductCON>
  );
};
